import React, { useMemo, useState, useEffect } from "react";

import DatePicker from "../../../../Components/DatePicker";
import CommonInput from "../../../../Components/Input";
import Select from "../../../../Components/Select";
import ToggleSwitch from "../../../../Components/ToggleSwitch";
import { isValidEmail } from "../../../../Utils/Constants/RegexConstants";
import {
  ROLES,
  isAccountTeamRole,
  isRole,
} from "../../../../Utils/Constants/RoleConstants";
import { toast } from "../../../../Utils/Helper/toast";
import FormModal from "../../../FormModal";
import "./style.scss";

interface AddUserModalProps {
  isOpen: boolean;
  toggle: () => void;
  organizations: { label: string; value: string }[];
  onSubmit: (data: any) => void;
  editData?: {
    fullName: string;
    email: string;
    role: string;
    position: string;
    phone: string;
    city: string;
    country: string;
    join_date: string;
    languages?: string;
    send_invitation: boolean;
  } | null;
  isEditMode?: boolean;
  roleOptions: { label: string; value: string }[];
  accountTeamRoleOptions: { label: string; value: string }[];
  loading?: boolean;
}

const initialFormData = {
  fullName: "",
  email: "",
  role: "",
  position: "",
  phone: "",
  city: "",
  country: "",
  join_date: "",
  languages: "",
  send_invitation: false,
};

/** Normalize API date (e.g. "2026-03-28T00:00:00") to YYYY-MM-DD for input type="date" */
const normalizeJoinDate = (dateStr: string | undefined): string => {
  if (!dateStr?.trim()) return "";
  const datePart = dateStr.trim().split("T")[0];
  return datePart && /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : dateStr;
};

const AddUserModal: React.FC<AddUserModalProps> = ({
  isOpen,
  toggle,
  onSubmit,
  editData,
  isEditMode = false,
  roleOptions,
  accountTeamRoleOptions,
  loading,
}) => {
  const [formData, setFormData] = useState(initialFormData);
  const [emailError, setEmailError] = useState("");
  const [wantToChangeRole, setWantToChangeRole] = useState(false);

  useEffect(() => {
    if (editData) {
      setFormData({
        fullName: editData.fullName,
        email: editData.email,
        role: editData.role,
        position: editData.position ?? "",
        phone: editData.phone ?? "",
        city: editData.city ?? "",
        country: editData.country ?? "",
        join_date: normalizeJoinDate(editData.join_date),
        languages: editData.languages ?? "",
        send_invitation: editData.send_invitation ?? false,
      });
      setEmailError("");
      setWantToChangeRole(false);
    } else {
      setFormData(initialFormData);
      setEmailError("");
      setWantToChangeRole(false);
    }
  }, [editData, isOpen]);

  const effectiveRole = useMemo(() => {
    if (isEditMode && !wantToChangeRole && editData) {
      return editData.role;
    }
    return formData.role;
  }, [isEditMode, wantToChangeRole, editData, formData.role]);

  const isExecutiveUser =
    isEditMode && !!editData && isRole(editData.role, ROLES.EXECUTIVE);

  const showAccountTeamFields = isAccountTeamRole(effectiveRole);

  const handleWantToChangeRole = (checked: boolean) => {
    if (checked && isExecutiveUser) {
      toast.info("Role cannot be changed for Executive users.");
      return;
    }
    setWantToChangeRole(checked);
  };

  const activeRoleOptions = showAccountTeamFields
    ? accountTeamRoleOptions
    : roleOptions;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const target = e.target;
    const name = target.name;
    if (isEditMode && name === "email") return;
    const value =
      target.type === "checkbox"
        ? (target as HTMLInputElement).checked
        : target.value;
    setFormData({ ...formData, [name]: value });

    if (name === "email") {
      const emailVal = typeof value === "string" ? value : "";
      if (emailVal.trim() === "") {
        setEmailError("");
      } else if (!isValidEmail(emailVal)) {
        setEmailError("Please enter a valid email address");
      } else {
        setEmailError("");
      }
    }
  };

  const handleSubmit = async () => {
    const basePayload =
      isEditMode && editData
        ? { ...formData, email: editData.email }
        : formData;
    const role =
      isEditMode && !wantToChangeRole && editData
        ? editData.role
        : formData.role;
    await onSubmit({ ...basePayload, role });
  };

  const roleRequired = !isEditMode || wantToChangeRole;
  const isFormValid = showAccountTeamFields
    ? formData.fullName.trim() !== "" &&
      formData.email.trim() !== "" &&
      isValidEmail(formData.email) &&
      (!roleRequired || formData.role.trim() !== "") &&
      formData.position.trim() !== "" &&
      !emailError
    : formData.fullName.trim() !== "" &&
      formData.email.trim() !== "" &&
      isValidEmail(formData.email) &&
      (!roleRequired || formData.role.trim() !== "") &&
      formData.position.trim() !== "" &&
      formData.city.trim() !== "" &&
      formData.country.trim() !== "" &&
      formData.join_date.trim() !== "" &&
      !emailError;

  return (
    <FormModal
      isOpen={isOpen}
      toggle={toggle}
      title={isEditMode ? "Edit User" : "Create New User"}
      onSubmit={handleSubmit}
      submitLabel={isEditMode ? "Update User" : "Create User"}
      isEditMode={isEditMode}
      loading={loading}
      disabled={!isFormValid}
    >
      <div className="add-user-modal">
        <CommonInput
          label="Full Name"
          name="fullName"
          value={formData.fullName}
          onChange={handleChange}
          placeholder="John Doe"
          required
        />
        <CommonInput
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="john@example.com"
          required
          error={emailError}
          disabled={isEditMode}
        />
        {isEditMode && (
          <ToggleSwitch
            className="add-user-modal__change-role"
            id="vendor-add-user-modal-change-role"
            label="Do you want to change the role?"
            checked={wantToChangeRole}
            onChange={handleWantToChangeRole}
          />
        )}
        {(wantToChangeRole || !isEditMode) && (
          <Select
            label="Role"
            name="role"
            value={formData.role}
            onChange={handleChange}
            options={activeRoleOptions}
            placeholder="Select Role"
            required={roleRequired}
          />
        )}
        <CommonInput
          label="Job Title"
          name="position"
          value={formData.position}
          onChange={handleChange}
          placeholder={
            showAccountTeamFields
              ? "Senior Account Executive"
              : "Senior Executive"
          }
          required
        />

        {showAccountTeamFields ? (
          <>
            <h6 className="add-user-modal__section-heading">
              Account Team Details
            </h6>
            <CommonInput
              label="Phone Number"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+1 (555) 123-4567"
            />
            <CommonInput
              label="Languages"
              name="languages"
              value={formData.languages}
              onChange={handleChange}
              placeholder="English, Spanish"
            />
          </>
        ) : (
          <>
            <h6 className="add-user-modal__section-heading">
              Additional Details
            </h6>
            <CommonInput
              label="Phone Number"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+1 (555) 123-4567"
            />
            <div className="add-user-modal__row flex flex-wrap-fields gap-4">
              <CommonInput
                label="City"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="San Francisco"
                className="w-full"
                required
              />
              <CommonInput
                label="Country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                placeholder="USA"
                className="w-full"
                required
              />
            </div>
            <DatePicker
              label="Join Date"
              name="join_date"
              value={formData.join_date}
              onChange={handleChange}
              placeholder="mm/dd/yyyy"
              required
            />
          </>
        )}

        {!isEditMode && (
          <div className="add-user-modal__checkbox checkbox-wrapper">
            <input
              type="checkbox"
              id="sendInvitation"
              name="send_invitation"
              checked={formData.send_invitation}
              onChange={handleChange}
            />
            <label htmlFor="sendInvitation" className="checkbox-label">
              Send invitation now?
            </label>
          </div>
        )}
      </div>
    </FormModal>
  );
};

export default AddUserModal;
